import type { DownloadTicket } from "@agentry/api-client";
import { useState } from "react";
import { Link, useParams } from "react-router";
import { useApi } from "../../app/ApiProvider";
import { useAuth, useScope } from "../../app/AuthProvider";
import { useAction, useAsync } from "../../app/useAsync";
import {
	Badge,
	Button,
	Callout,
	Card,
	DefinitionList,
	Field,
	LinkButton,
	PageHeader,
	Select,
	Table,
	TextInput,
} from "../../ui/primitives";
import {
	EmptyState,
	ErrorState,
	isNotFound,
	LoadingState,
	NotFoundState,
	recoveryFor,
} from "../../ui/states";
import { renderMarkdown } from "./markdown";
import { formatDateTime } from "./ProgressPages";

export function ArtifactListPage() {
	const { client } = useApi();
	const scope = useScope();
	const data = useAsync(
		() => client.listArtifacts(scope, { limit: 200 }),
		[client, scope.tenantId, scope.projectId],
	);

	return (
		<>
			<PageHeader
				eyebrow="成果物"
				title="できあがったもの"
				description="実行環境が作り直されても、成果物は保管庫から取り出せます。"
			/>
			{data.status === "loading" && <LoadingState />}
			{data.status === "error" && <ErrorState error={data.error} onRetry={data.reload} />}
			{data.status === "ready" &&
				(data.data.items.length === 0 ? (
					<EmptyState
						title="まだ成果物がありません"
						description="依頼が完了するとここに表示されます。"
					/>
				) : (
					<Card>
						<Table head={["名前", "版", "形式", "サイズ", "作成日時", ""]}>
							{data.data.items.map((artifact) => (
								<tr key={artifact.id}>
									<td>
										<Link
											to={`/app/artifacts/${artifact.id}`}
											className="font-semibold text-ink-900 hover:text-brand-600"
										>
											{artifact.name}
										</Link>
									</td>
									<td>
										<Badge tone="neutral">v{artifact.version}</Badge>
									</td>
									<td className="text-ink-600">{artifact.mimeType}</td>
									<td className="text-ink-600">{artifact.sizeBytes.toLocaleString()} B</td>
									<td className="text-ink-600">{formatDateTime(artifact.createdAt)}</td>
									<td>
										<LinkButton to={`/app/artifacts/${artifact.id}`} variant="secondary" size="sm">
											開く
										</LinkButton>
									</td>
								</tr>
							))}
						</Table>
					</Card>
				))}
		</>
	);
}

export function ArtifactDetailPage() {
	const { artifactId = "" } = useParams();
	const { client } = useApi();
	const { session } = useAuth();
	const scope = useScope();
	const [reloadSeq, setReloadSeq] = useState(0);
	const [ticket, setTicket] = useState<DownloadTicket | null>(null);
	const [transferTo, setTransferTo] = useState("");
	const [transferReason, setTransferReason] = useState("");

	const data = useAsync(async () => {
		// 主要データはここで失敗させる。補助データの欠落でページ全体を落とさない。
		const content = await client.getArtifactContent(scope, artifactId);
		const [versions, transfers, storage] = await Promise.all([
			client.listArtifactVersions(scope, artifactId).catch(() => [content.artifact]),
			client.listOwnershipTransfers(scope, artifactId).catch(() => []),
			client.listStorageObjects(scope).catch(() => []),
		]);
		return { content, versions, transfers, storage };
	}, [client, scope.tenantId, scope.projectId, artifactId, reloadSeq]);

	const download = useAction(async () => {
		const created = await client.createDownloadTicket(scope, artifactId);
		setTicket(created);
		return created;
	});

	const transfer = useAction(async () =>
		client.transferArtifactOwnership(scope, artifactId, {
			toOwnerUserId: transferTo,
			reason: transferReason,
		}),
	);

	if (data.status === "loading") return <LoadingState label="成果物を取得しています" />;
	if (data.status === "error") {
		if (isNotFound(data.error)) {
			return <NotFoundState detail="この成果物にはアクセスできません。" />;
		}
		return <ErrorState error={data.error} onRetry={() => setReloadSeq((n) => n + 1)} />;
	}

	const { content, versions, transfers, storage } = data.data;
	const artifact = content.artifact;
	const storageObject = storage.find((s) => s.artifactId === artifact.id);
	const isOwner = artifact.ownerUserId === session?.user.id;
	const role = session?.membership.role;
	const canTransfer = isOwner || role === "tenant_admin" || role === "platform_admin";
	const expired = ticket ? Date.parse(ticket.expiresAt) < Date.now() : false;
	const isJson = artifact.mimeType === "application/json" || artifact.mimeType.endsWith("+json");
	const previewContent = isJson ? formatJson(content.content) : content.content;

	return (
		<>
			<PageHeader
				eyebrow="成果物"
				title={artifact.name}
				description={`版 ${artifact.version} · ${artifact.type}`}
				actions={
					<>
						<Button variant="secondary" onClick={() => setReloadSeq((n) => n + 1)}>
							取り直す
						</Button>
						<Button onClick={() => void download.run()} disabled={download.pending}>
							{download.pending ? "準備中…" : "ダウンロード"}
						</Button>
					</>
				}
			/>

			{content.checksumVerified && (
				<Callout tone="success" title="内容の検証に成功しました">
					保存時のチェックサムと一致しました。表示している内容は改変されていません。
				</Callout>
			)}

			{ticket && (
				<div className="mt-3">
					<Callout
						tone={expired ? "warning" : "neutral"}
						title={expired ? "ダウンロードURLの期限が切れました" : "ダウンロードURLを発行しました"}
					>
						{expired ? (
							<>
								<p className="mt-1">期限切れのURLは使えません。もう一度発行してください。</p>
								<Button size="sm" className="mt-2" onClick={() => void download.run()}>
									URLを再発行する
								</Button>
							</>
						) : (
							<>
								<p className="mt-1 font-mono text-[11.5px] break-all text-ink-600">{ticket.url}</p>
								<p className="mt-1">
									有効期限: {formatDateTime(ticket.expiresAt)} ／ チェックサム:{" "}
									<span className="font-mono text-[11px]">{ticket.checksum.slice(0, 24)}…</span>
								</p>
							</>
						)}
					</Callout>
				</div>
			)}

			<div className="mt-5 grid gap-4 lg:grid-cols-[1fr_340px]">
				<Card title="内容">
					{isJson ? (
						<pre className="overflow-x-auto whitespace-pre-wrap break-words rounded-lg bg-ink-50 p-4 font-mono text-[12.5px] leading-6 text-ink-800">
							{previewContent}
						</pre>
					) : artifact.mimeType.startsWith("text/") ? (
						<div
							className="ag-prose"
							// biome-ignore lint/security/noDangerouslySetInnerHtml: renderMarkdownはescape後に限定タグのみ生成する
							dangerouslySetInnerHTML={{ __html: renderMarkdown(content.content) }}
						/>
					) : (
						<EmptyState
							title="この形式はプレビューできません"
							description="ダウンロードして確認してください。"
						/>
					)}
				</Card>

				<div className="space-y-4">
					<Card title="この成果物の情報">
						<DefinitionList
							columns={1}
							items={[
								{
									label: "所有者",
									value: isOwner ? `${session?.user.name}（あなた）` : artifact.ownerUserId,
								},
								{ label: "作成したもの", value: artifact.generatedByActorId },
								{ label: "版", value: `v${artifact.version}` },
								{ label: "形式", value: artifact.mimeType },
								{ label: "サイズ", value: `${artifact.sizeBytes.toLocaleString()} bytes` },
								{ label: "チェックサム", value: artifact.checksum, mono: true },
								{ label: "作成日時", value: formatDateTime(artifact.createdAt) },
							]}
						/>
						<p className="mt-3 text-[12px] text-ink-500">
							所有者は、この成果物が作られたときの依頼の担当者です。使ったAI契約の持ち主とは別です。
						</p>
					</Card>

					<Card title="保存先">
						{storageObject ? (
							<DefinitionList
								columns={1}
								items={[
									{
										label: "保管区分",
										value: storageObject.storageClass === "ARCHIVE" ? "長期保管" : "標準",
									},
									{ label: "参照", value: storageObject.objectRef, mono: true },
									{
										label: "保管期限",
										value: storageObject.retentionUntil
											? formatDateTime(storageObject.retentionUntil)
											: "期限なし",
									},
								]}
							/>
						) : (
							<EmptyState title="保存先情報がありません" />
						)}
						<LinkButton to="/app/storage" variant="secondary" size="sm" className="mt-3 w-full">
							保管庫を開く
						</LinkButton>
					</Card>

					<Card title="版の履歴">
						{versions.length <= 1 ? (
							<p className="text-[13px] text-ink-500">この成果物は1版のみです。</p>
						) : (
							<ul className="space-y-1.5">
								{versions.map((version) => (
									<li
										key={version.id}
										className="flex items-center justify-between gap-2 text-[13.5px]"
									>
										<Link
											to={`/app/artifacts/${version.id}`}
											className={
												version.id === artifact.id
													? "font-semibold text-brand-700"
													: "text-ink-700 hover:text-brand-600"
											}
										>
											v{version.version}
											{version.id === artifact.id && "（表示中）"}
										</Link>
										<span className="text-[12px] text-ink-500">
											{formatDateTime(version.createdAt)}
										</span>
									</li>
								))}
							</ul>
						)}
					</Card>

					<Card title="所有者の移管">
						{transfers.length > 0 && (
							<ul className="mb-3 space-y-1.5 text-[12.5px] text-ink-600">
								{transfers.map((record) => (
									<li key={record.id}>
										{record.fromOwnerLabel} → {record.toOwnerLabel}（{record.reason}） ·{" "}
										{formatDateTime(record.transferredAt)}
									</li>
								))}
							</ul>
						)}
						{canTransfer ? (
							<form
								className="space-y-3"
								onSubmit={async (event) => {
									event.preventDefault();
									const result = await transfer.run();
									if (result) {
										setTransferTo("");
										setTransferReason("");
										setReloadSeq((n) => n + 1);
									}
								}}
							>
								<Field label="新しい所有者">
									<Select
										value={transferTo}
										onChange={(event) => setTransferTo(event.target.value)}
										required
									>
										<option value="">選択してください</option>
										<option value="user-01">田中 彩</option>
										<option value="user-02">佐藤 健</option>
										<option value="user-04">鈴木 涼</option>
									</Select>
								</Field>
								<Field label="理由" hint="監査ログに残ります">
									<TextInput
										value={transferReason}
										onChange={(event) => setTransferReason(event.target.value)}
										required
									/>
								</Field>
								{transfer.error != null && (
									<p role="alert" className="text-[12.5px] font-semibold text-danger-600">
										{recoveryFor(transfer.error).title}
									</p>
								)}
								<Button
									type="submit"
									variant="secondary"
									size="sm"
									className="w-full"
									disabled={transfer.pending}
								>
									移管する
								</Button>
							</form>
						) : (
							<p className="text-[13px] text-ink-500">
								閲覧はできますが、所有者を変更する権限はありません。
							</p>
						)}
					</Card>
				</div>
			</div>
		</>
	);
}

function formatJson(value: string): string {
	try {
		return JSON.stringify(JSON.parse(value), null, 2);
	} catch {
		return value;
	}
}
